import { useAuthorizedRequest } from '@/hooks/useRequest';
import type { Client } from '@/lib/Client';
import { t } from '@/lib/i18n';
import { Box, Button, Heading, Skeleton, Table, Text, Tooltip, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

interface ClientDebtReport {
    client: Client;
    paid: number;
    used: number;
    pending: number;
    debt: number;
}

export function MostIndebted() {
    const navigate = useNavigate();
    const { data, error, isLoading, isAuthenticated } =
        useAuthorizedRequest<{ reports: ClientDebtReport[] }>('/reports/most-indebted?limit=50', jsonFetcher);

    const reports = useMemo(() => data?.reports ?? [], [data]);

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')}: {(error as Error).message}</div>;

    const indebted = reports.filter(r => r.debt < 0);

    return (
        <VStack gap={4} align="stretch">
            <Heading size="md">{t('reports.most_indebted')}</Heading>
            <Text fontSize="sm" color="gray.500">
                {t('reports.debt_description')}
            </Text>
            {isLoading ? (
                <Box overflowX="auto">
                    <Table.Root variant="line" showColumnBorder interactive size="sm">
                        <Table.Header>
                            <Table.Row>
                                <Table.ColumnHeader>{t('label.client')}</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.paid')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.used')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.pending')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.debt')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {[...Array(5)].map((_, i) => (
                                <Table.Row key={i}>
                                    {[...Array(6)].map((_, j) => (
                                        <Table.Cell key={j}><Skeleton height="20px" /></Table.Cell>
                                    ))}
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>
            ) : indebted.length === 0 ? (
                <Text color="gray.500">{t('reports.no_debt')}</Text>
            ) : (
                <Box overflowX="auto">
                    <Table.Root variant="line" showColumnBorder interactive size="sm">
                        <Table.Header>
                            <Table.Row>
                                <Table.ColumnHeader>{t('label.client')}</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.paid')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.used')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.pending')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.debt')} (₴)</Table.ColumnHeader>
                                <Table.ColumnHeader>{t('label.actions')}</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {indebted.map(r => (
                                <Table.Row key={r.client.id}>
                                    <Table.Cell>{r.client.name}</Table.Cell>
                                    <Table.Cell>{r.paid}</Table.Cell>
                                    <Table.Cell>{r.used}</Table.Cell>
                                    <Table.Cell>{r.pending}</Table.Cell>
                                    <Table.Cell color={r.debt < 0 ? 'red.500' : 'green.500'}>
                                        {r.debt}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Tooltip.Root>
                                            <Tooltip.Trigger asChild>
                                                <Button size="xs" variant="ghost" onClick={() => navigate(`/client/${r.client.id}`)} aria-label={t('action.edit')}>✏️</Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Positioner>
                                                <Tooltip.Content>{t('action.edit')}</Tooltip.Content>
                                            </Tooltip.Positioner>
                                        </Tooltip.Root>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>
                </Box>
            )}
        </VStack>
    );
}
